import React, { useState } from 'react';
import { Tooltip, Row, Col, Avatar } from 'antd';
import { isEmpty } from 'lodash';
import { TransactionCreativeExpandedCard } from './TransactionCreativeExpandedCard';

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
  TooltipText,
} from '../../../../../../../shared/globalStyling/styledText';
import { formatWholeNumber } from '../../../../../../../../core/utils/campaigns';
import uuid from 'uuid';
import SimpleTable from '../../../../../../../shared/SimpleTable';
import { CloudOutlined } from '@ant-design/icons';

const TransactionCreativeTable = ({ creativeData }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const extraColumn = {
    title: '',
    dataIndex: 'media',
    key: 'media',
    width: '70px',
    align: 'left',
    render: (media) => {
      const url = typeof media === 'string' ? media : null;
      return (
        <Avatar
          shape="square"
          size={50}
          icon={<CloudOutlined />}
          src={url === '' || !url ? false : url}
          style={{ marginLeft: '-16px' }}
        />
      );
    },
  };

  const columns = [
    {
      title: '',
      dataIndex: 'color',
      key: 'color',
      width: '15px',
      render: (text) => {
        return (
          <Row
            type="flex"
            justify="center"
            align="middle"
            style={{
              width: '100%',
            }}
          >
            <div
              style={{
                backgroundColor: text,
                height: '15px',
                width: '15px',
                minWidth: '15px',
                borderRadius: '50px',
              }}
            />
          </Row>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Name'} />,
      dataIndex: 'name',
      key: 'name',
      width: '250px',
      align: 'left',
      footerContent: 'Totals + Averages',
      sorter: (a, b) => a.value.localeCompare(b.value),
      render: (text, record) => {
        if (text) {
          return (
            <Row
              type="flex"
              justify="start"
              align="middle"
              style={{
                width: '100%',
              }}
            >
              <Col span={24}>
                {text.length > 45 ? (
                  <Tooltip title={<TooltipText text={text} />}>
                    <span>
                      <PrimaryTableRowText text={text.slice(0, 46).concat('...')} />
                    </span>
                  </Tooltip>
                ) : (
                  <PrimaryTableRowText text={text} />
                )}
              </Col>
            </Row>
          );
        } else {
          return <div />;
        }
      },
    },
    {
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'impressions',
      footerSum: true,
      key: 'impressions',
      width: '130px',
      align: 'left',
      sorter: (a, b) => a.impressions - b.impressions,
      render: (impressions) => (
        <PrimaryTableRowText text={impressions && formatWholeNumber(impressions)} />
      ),
    },
    {
      title: <TableColumnTitle text={'Transactions'} />,
      dataIndex: 'transactions',
      footerSum: true,
      key: 'transactions',
      width: '130px',
      align: 'left',
      sorter: (a, b) => a.transactions - b.transactions,
      render: (transactions) => (
        <PrimaryTableRowText text={transactions && formatWholeNumber(transactions)} />
      ),
    },
  ];

  const checkIfExpandable = (record) => {
    if (!record.media || record.media === '') return false;
    const hasMediaUrl = typeof record.media === 'string' && record.media.trim() !== '';
    const hasMediaObject = typeof record.media === 'object' && record.media.key;
    return hasMediaUrl || hasMediaObject;
  };

  const hasAnyMedia =
    creativeData &&
    !isEmpty(creativeData) &&
    creativeData.some((creative) => checkIfExpandable(creative));

  if (hasAnyMedia) {
    columns.unshift(extraColumn);
  }

  // Temporarily hide the Impressions and Transactions columns
  const visibleColumns = columns.filter(
    (col) => col.key !== 'impressions' && col.key !== 'transactions'
  );

  const tableProps = {
    footer: creativeData.slice(currentPage * 10 - 10, currentPage * 10),
    bordered: false,
    columns: visibleColumns,
    data: creativeData.map((creative) => {
      return {
        ...creative,
        key: `${creative.id}${uuid.v4()}`,
      };
    }),
    scroll: { x: 200, y: 400 },
    onChange: (paginationObject, filtersObject, sorterObject) => {
      setCurrentPage(paginationObject.current);
    },
    pagination: {
      pageSize: 10,
      total: creativeData.length,
      current: currentPage,
    },
  };

  if (hasAnyMedia) {
    tableProps.expandable = {
      expandedRowRender: (record) => record && <TransactionCreativeExpandedCard record={record} />,
      rowExpandable: (record) => checkIfExpandable(record),
    };
    tableProps.rowClassName = (record) => {
      return checkIfExpandable(record) ? '' : 'hide-expand';
    };
  }

  return <SimpleTable {...tableProps} />;
};

export default TransactionCreativeTable;
